import { Link } from "wouter";
import { ReactNode } from "react";

/**
 * The opening band for a travel-guide article or category.
 *
 * Every other page on the site opens with a hero - video or photo - and the
 * blog was the one section that did not: articles started with a breadcrumb and
 * a green pill badge directly under the header, which is why they read as a
 * generic template bolted onto a premium site.
 *
 * None of the 113 posts carry imagery, so this leans on the brand's typographic
 * furniture instead of inventing photos that do not belong to the subject: the
 * sand gradient band from PageHero's muted variant, a driftwood eyebrow, the
 * forest serif headline and the diamond rule used in the video heroes.
 */
interface ArticleMastheadProps {
  /** Small letterspaced label above the title - usually the category. */
  eyebrow?: string;
  /** Where the eyebrow links, if anywhere. */
  eyebrowHref?: string;
  title: ReactNode;
  /** The standfirst under the rule. Real editorial copy, not generated. */
  deck?: ReactNode;
  /** Date, reading time and the like, rendered as a letterspaced meta row. */
  meta?: string[];
  children?: ReactNode;
}

export function ArticleMasthead({
  eyebrow,
  eyebrowHref,
  title,
  deck,
  meta,
  children,
}: ArticleMastheadProps) {
  return (
    <section className="relative overflow-hidden border-b border-sand-deep bg-[linear-gradient(158deg,#F7EDDB_0%,#F0E4CE_100%)] pt-28 pb-14 md:pt-36 md:pb-20">
      <div
        className="pointer-events-none absolute right-[-120px] top-[-140px] h-[440px] w-[440px] rounded-full bg-[radial-gradient(circle,rgba(47,94,52,0.14)_0%,rgba(47,94,52,0)_70%)]"
        aria-hidden="true"
      />

      <div className="container relative z-10 mx-auto max-w-3xl px-6 text-center">
        {eyebrow &&
          (eyebrowHref ? (
            <Link
              href={eyebrowHref}
              className="mb-5 inline-block font-sans text-[11px] uppercase tracking-[0.3em] text-driftwood transition-colors hover:text-forest"
              data-testid="link-masthead-eyebrow"
            >
              {eyebrow}
            </Link>
          ) : (
            <span className="mb-5 inline-block font-sans text-[11px] uppercase tracking-[0.3em] text-driftwood">
              {eyebrow}
            </span>
          ))}

        <h1 className="font-serif font-normal leading-[1.08] tracking-tight text-forest text-[clamp(28px,5vw,52px)]">
          {title}
        </h1>

        <div className="mx-auto mt-6 mb-6 flex w-full max-w-[min(70vw,380px)] items-center gap-3">
          <span className="h-px flex-1 bg-sand-deep" />
          <span className="h-[6px] w-[6px] rotate-45 bg-driftwood" />
          <span className="h-px flex-1 bg-sand-deep" />
        </div>

        {deck && (
          <p
            className="article-deck mx-auto max-w-[620px] font-serif text-[clamp(15px,2.2vw,20px)] italic leading-relaxed text-ink-soft"
            data-testid="text-masthead-deck"
          >
            {deck}
          </p>
        )}

        {meta && meta.length > 0 && (
          <p className="mt-8 font-sans text-[11px] uppercase tracking-[0.24em] text-driftwood">
            {meta.filter(Boolean).map((item, i) => (
              <span key={item}>
                {i > 0 && <span className="mx-3 text-sand-deep">/</span>}
                {item}
              </span>
            ))}
          </p>
        )}

        {children}
      </div>
    </section>
  );
}
